import { combineReducers } from 'redux'
import {
  generateAsyncReducer,
  generateFetchActionTypes,
  defaultState,
} from 'store/helpers'
import cookie from 'lib/cookie'
import { getDeviceId } from 'lib/user'

/**
 * Action Type
 */
export const refreshAccessTokenActionType =
  generateFetchActionTypes('AUTH/REFRESH_TOKEN')
export const loginWithEmailActionType = generateFetchActionTypes(
  'AUTH/LOGIN_WITH_EMAIL'
)
export const logoutActionType = generateFetchActionTypes('AUTH/LOGOUT')
export const addActionToRetryQueueActionType = {
  add: 'AUTH/ACTION_TO_RETRY_QUEUE_ADD',
  pop: 'AUTH/ACTION_TO_RETRY_QUEUE_POP',
  clear: 'AUTH/ACTION_TO_RETRY_QUEUE_CLEAR',
}

// new action types for refresh token management
export const refreshTokenStateActionType = {
  setInProgress: 'AUTH/REFRESH_TOKEN_SET_IN_PROGRESS',
  clearInProgress: 'AUTH/REFRESH_TOKEN_CLEAR_IN_PROGRESS',
  updateLastRefresh: 'AUTH/UPDATE_LAST_REFRESH_TIME',
  incrementRetryCount: 'AUTH/INCREMENT_RETRY_COUNT',
  scheduleRetry: 'AUTH/SCHEDULE_RETRY',
  clearScheduledRetry: 'AUTH/CLEAR_SCHEDULED_RETRY',
}

/**
 * Helpers
 */
export const generateRefreshId = () =>
  `refresh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

/**
 * Actions
 */
export const loginWithEmail = ({
  email,
  password,
  redirectUrl,
  two_fa_code,
}: {
  email: string
  password: string
  redirectUrl: string
  two_fa_code?: string
}) => {
  return {
    type: loginWithEmailActionType.request,
    payload: {
      client: 'bare',
      request: {
        url: '/v2/users/login_with_email',
        method: 'POST',
        data: {
          email,
          password,
          two_fa_code,
          api_key: window?.gon?.API_KEY,
          uuid: getDeviceId(),
        },
      },
    },
    meta: {
      redirectUrl,
    },
  }
}

export const logout = () => {
  return {
    type: logoutActionType.request,
    payload: {
      client: 'default',
      request: {
        method: 'POST',
        url: '/v1/users/logout',
        data: {
          uuid: getDeviceId(),
        },
      },
    },
  }
}

// enhanced refreshAccessToken action with unique ID tracking
export const refreshAccessToken = (
  options: { refreshId?: string; retryCount?: number } = {}
) => {
  const formData = new FormData()
  formData.set('grant_type', 'refresh_token')
  formData.set('refresh_token', cookie.refreshToken.get())

  const refreshId = options.refreshId || generateRefreshId()
  const retryCount = options.retryCount || 0

  return {
    type: refreshAccessTokenActionType.request,
    payload: {
      client: 'default',
      request: {
        url: '/api/v1/oauth/token',
        method: 'POST',
        data: formData,
        headers: {
          Authorization: `Basic ${window?.btoa?.(window?.gon?.API_KEY)}`,
        },
      },
    },
    meta: {
      refreshId,
      retryCount,
    },
  }
}

export const addActionToRetryQueue = (action: any) => {
  return {
    type: addActionToRetryQueueActionType.add,
    payload: action,
  }
}

// new actions for refresh token state management
export const setRefreshInProgress = (refreshId: string) => ({
  type: refreshTokenStateActionType.setInProgress,
  payload: { refreshId, timestamp: Date.now() },
})

export const clearRefreshInProgress = (refreshId: string) => ({
  type: refreshTokenStateActionType.clearInProgress,
  payload: { refreshId },
})

export const updateLastRefreshTime = () => ({
  type: refreshTokenStateActionType.updateLastRefresh,
  payload: { timestamp: Date.now() },
})

export const incrementRetryCount = (refreshId: string) => ({
  type: refreshTokenStateActionType.incrementRetryCount,
  payload: { refreshId },
})

export const scheduleTokenRetry = (
  refreshId: string,
  retryCount: number,
  scheduledTime: number
) => ({
  type: refreshTokenStateActionType.scheduleRetry,
  payload: { refreshId, retryCount, scheduledTime },
})

export const clearScheduledRetry = (refreshId: string) => ({
  type: refreshTokenStateActionType.clearScheduledRetry,
  payload: { refreshId },
})

/**
 * Selectors
 */
export const selectLoginWithEmail = (state: any) =>
  state?.auth?.loginWithEmail || defaultState
export const selectLogout = (state: any) => state?.auth?.logout || defaultState
export const selectRefreshAccessToken = (state: any) =>
  state?.auth?.refreshAccessToken || defaultState
export const selectRetryQueue = (state: any) => state?.auth?.retryQueue || []

// new selectors for refresh token state
export const selectRefreshTokenState = (state: any) =>
  state.auth.refreshToken || {}

export const selectRefreshInProgress = (state: any) => {
  const now = Date.now()
  const refreshes = Object.values(
    selectRefreshTokenState(state).inProgress || {}
  )

  // if refresh is less than 10 seconds old consider it in progress
  return refreshes.some((timestamp: number) => now - timestamp < 10000)
}

export const selectLastRefreshTime = (state: any) =>
  selectRefreshTokenState(state).lastRefreshTime || 0

export const selectRetryCount = (state: any, refreshId: string) =>
  (selectRefreshTokenState(state).retryCounts || {})[refreshId] || 0

export const selectScheduledRetries = (state: any) =>
  selectRefreshTokenState(state).scheduledRetries || {}

/**
 * Reducer
 */

// initial state for the refresh token portion
const initialRefreshTokenState = {
  inProgress: {}, // { [refreshId]: timestamp }
  lastRefreshTime: 0,
  retryCounts: {}, // { [refreshId]: count }
  scheduledRetries: {}, // { [refreshId]: { count, scheduledTime } }
}

function refreshTokenReducer(state = initialRefreshTokenState, action) {
  switch (action.type) {
    case refreshTokenStateActionType.setInProgress:
      return {
        ...state,
        inProgress: {
          ...state.inProgress,
          [action.payload.refreshId]: action.payload.timestamp,
        },
      }

    case refreshTokenStateActionType.clearInProgress:
      const newInProgress = { ...state.inProgress }
      delete newInProgress[action.payload.refreshId]
      return {
        ...state,
        inProgress: newInProgress,
      }

    case refreshTokenStateActionType.updateLastRefresh:
      return {
        ...state,
        lastRefreshTime: action.payload.timestamp,
      }

    case refreshTokenStateActionType.incrementRetryCount:
      return {
        ...state,
        retryCounts: {
          ...state.retryCounts,
          [action.payload.refreshId]:
            (state.retryCounts[action.payload.refreshId] || 0) + 1,
        },
      }

    case refreshTokenStateActionType.scheduleRetry:
      return {
        ...state,
        scheduledRetries: {
          ...state.scheduledRetries,
          [action.payload.refreshId]: {
            retryCount: action.payload.retryCount,
            scheduledTime: action.payload.scheduledTime,
          },
        },
      }

    case refreshTokenStateActionType.clearScheduledRetry:
      const newScheduledRetries = { ...state.scheduledRetries }
      delete newScheduledRetries[action.payload.refreshId]
      return {
        ...state,
        scheduledRetries: newScheduledRetries,
      }

    case refreshAccessTokenActionType.success:
      // clear in-progress status for this refresh ID
      if (action.meta?.refreshId) {
        const successInProgress = { ...state.inProgress }
        delete successInProgress[action.meta.refreshId]

        return {
          ...state,
          inProgress: successInProgress,
          lastRefreshTime: Date.now(),
        }
      }
      return {
        ...state,
        lastRefreshTime: Date.now(),
      }

    default:
      return state
  }
}

export const authReducer = combineReducers({
  loginWithEmail: generateAsyncReducer({
    defaultState,
    actionType: loginWithEmailActionType,
  }),
  logout: generateAsyncReducer({
    defaultState,
    actionType: logoutActionType,
  }),
  refreshAccessToken: generateAsyncReducer({
    defaultState,
    actionType: refreshAccessTokenActionType,
  }),
  retryQueue: (state: Array<unknown> = [], action) => {
    switch (action.type) {
      case addActionToRetryQueueActionType.add:
        return [...state, action.payload]
      case addActionToRetryQueueActionType.pop:
        return [...state.slice(1)]
      case addActionToRetryQueueActionType.clear:
        return []
      default:
        return state
    }
  },
  refreshToken: refreshTokenReducer,
})
